import produce from "immer"

import {
    LOW_DEMAND_MODE,
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    NO_ARCHITECTURAL_CONSTRAINTS,
    FIT,
    SCIENTIFIC,
    PERCENT,
    YES,
} from "const"
import { deepCopy, InferValueTypes } from "client/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const initUserSettings: types.UserSettings = {
    project: {
        defaultPublicId: "",
        defaultName: "Project",
        defaultDescription: "<i>Describe your project</i>",
        defaultLogoId: null,
        defaultLogo: null,
        defaultModules: {},
    },
    sil: {
        defaultSafetyFunctionName: "",
        defaultMode: LOW_DEMAND_MODE,
        defaultCalculationMethodDangerous: NO_DANGEROUS_FAILURE,
        defaultCalculationMethodSafe: NO_SAFE_FAILURE,
        defaultArchitecturalConstraints: NO_ARCHITECTURAL_CONSTRAINTS,
        // defaultReportTemplate ----- TODO

        failureRate: FIT,
        failureRateDecimal: 2,
        probability: SCIENTIFIC,
        probabilityDecimal: 2,
        percentage: PERCENT,
        percentageDecimal: 2,

        // IEC 61508:2010
        iec615082010: {
            t1: "8760",
            mttr: "72",
            mrt: "72",
            beta: "0.05",
            betaD: "0.025",
            k: "0.35",
        },

        // IEC 61508:1998
        iec615081998: {
            t1: "8760",
            mttr: "72",
            beta: "0.05",
        },

        // ISA TR84:2002
        isaTR842002: {
            t1: "8760",
            mttr: "72",
            beta: "0.05",
            safeFailure: "0.05",
            dangerousFailure: "0.05",
        },

        // VDI/VDE 2180:2019
        vdivde21802019: {
            ptc0A: "0.3",
            ptc1A: "0.7",
            ptc0B: "0.4",
            ptc1B: "0.6",
            ptc0C: "0.2",
            ptc1C: "0.8",
            beta: "0.05",
            t0: "730",
            t1: "8760",
            t2: "87600",
        },

        // ISA TR84:2022
        isaTR842022: {
            ti: "8760",
            di: "87",
            mttr: "72",
            beta: "0.05",
            safeState: YES,
        },
    },
    system: {
        rowsPerPage: 10,
    },
    displayColumns: {
        compliance: {},
        formalReviewCompliance: {},
        reviewCompliance: {},
        reviewReview: {},
        requirementReview: {},
        task: {},
    },
}

const initUserSettingsState: types.UserSettingsState = {
    settings: initUserSettings,
    reloadList: true,
}

const userSettingsReducer = produce((
    draft: types.UserSettingsState = initUserSettingsState,
    action: ActionTypes = null,
): types.UserSettingsState => {
    switch (action.type) {
        case types.SET_USER_SETTINGS:
            draft.settings = action.settings
            return draft

        case types.UPDATE_USER_SETTINGS: {
            const { section, settings } = action.data
            const newSettings = deepCopy(draft.settings)
            Object.keys(settings).forEach((settingId) => {
                if (newSettings[section]) {
                    newSettings[section][settingId] = settings[settingId]
                }
            })
            draft.settings = <types.UserSettings>newSettings
            return draft
        }

        case types.USER_SETTINGS_RELOAD_LIST:
            draft.reloadList = action.value
            return draft

        default:
            return draft
    }
})
export default userSettingsReducer
